import { useContext, useMemo } from 'react';
import { ConfiguratorContext } from '@/components/Configurator/ConfiguratorContext';
import { useDisplayContext, useTranslator } from '@canvas-logic/clp-shared-ui';
import { labelId, type PtStep } from '@canvas-logic/clp-localization';

/**
 * Component-slot product translator — the slot's filler product type can be
 * basic or composite, so callers don't have to discriminate. The label IDs
 * follow ADR-0045 v5 grammar; resolution runs through the entity cascade so
 * scoped overrides (cpt → cpt.prod → cpt.cmp → …) are honoured.
 */
export interface ComponentProductTranslator {
  /** Translate a specific product's name (e.g. `bpt.shoe.prod.runner.name`). */
  productName(productKey: string, systemValue: string): string;
  /** Translate a specific product's description. */
  productDesc(productKey: string, systemValue: string): string;
  /** Translate the slot's filler PT name (e.g. `bpt.shoe.name`). */
  ptName(systemValue: string): string;
  /** Translate the slot's filler PT description. */
  ptDesc(systemValue: string): string;
}

const identityComponentProduct: ComponentProductTranslator = {
  productName: (_k, systemValue) => systemValue,
  productDesc: (_k, systemValue) => systemValue,
  ptName: (systemValue) => systemValue,
  ptDesc: (systemValue) => systemValue,
};

type ProductTypeResolver = {
  getComponentProductType?: (componentKey: string) => { key: string; isComposite: boolean } | undefined;
};

function useResolvedProductType(componentKey: string): { key: string; isComposite: boolean } | undefined {
  const ctx = useContext(ConfiguratorContext);
  const api = ctx?.sdk?.api as ProductTypeResolver | undefined;
  return api?.getComponentProductType?.(componentKey);
}

export function useComponentProductTypeTranslation(componentKey: string): ComponentProductTranslator {
  const pt = useResolvedProductType(componentKey);
  const translator = useTranslator();
  const displayCtx = useDisplayContext();

  return useMemo<ComponentProductTranslator>(() => {
    if (!pt) return identityComponentProduct;
    const step: PtStep = pt.isComposite ? 'cpt' : 'bpt';
    const lookup = (canonical: string, systemValue: string): string =>
      translator.entity(canonical, displayCtx, systemValue);
    return {
      productName: (productKey, systemValue) => lookup(labelId.productName(step, pt.key, productKey), systemValue),
      productDesc: (productKey, systemValue) => lookup(labelId.productDesc(step, pt.key, productKey), systemValue),
      ptName: (systemValue) => lookup(labelId.productTypeName(step, pt.key), systemValue),
      ptDesc: (systemValue) => lookup(labelId.productTypeDesc(step, pt.key), systemValue),
    };
  }, [pt?.key, pt?.isComposite, translator, displayCtx]);
}

/**
 * Translator for the configurator's root composite product (the one rendered
 * by `CompositeProductInfo`). The PT step is fixed to `cpt` because the
 * configurator only mounts this component for composite roots.
 */
export interface CompositeProductSelfTranslator {
  name(productKey: string | undefined, systemValue: string): string;
  description(productKey: string | undefined, systemValue: string): string;
}

const identityCompositeSelf: CompositeProductSelfTranslator = {
  name: (_k, systemValue) => systemValue,
  description: (_k, systemValue) => systemValue,
};

export function useCompositeProductSelfTranslation(productTypeKey: string | undefined): CompositeProductSelfTranslator {
  const translator = useTranslator();
  const displayCtx = useDisplayContext();

  return useMemo<CompositeProductSelfTranslator>(() => {
    if (!productTypeKey) return identityCompositeSelf;
    const lookup = (canonical: string, systemValue: string): string =>
      translator.entity(canonical, displayCtx, systemValue);
    return {
      name: (productKey, systemValue) =>
        productKey
          ? lookup(labelId.productName('cpt', productTypeKey, productKey), systemValue)
          : lookup(labelId.productTypeName('cpt', productTypeKey), systemValue),
      description: (productKey, systemValue) =>
        productKey
          ? lookup(labelId.productDesc('cpt', productTypeKey, productKey), systemValue)
          : lookup(labelId.productTypeDesc('cpt', productTypeKey), systemValue),
    };
  }, [productTypeKey, translator, displayCtx]);
}
